import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaCheck, FaChevronDown } from 'react-icons/fa';
import { useI18n } from '../../i18n';
import FlagIcon from './FlagIcon';
import './LanguageSwitcher.css';

/**
 * Compact language picker for the navbar.
 * @param {{ variant?: 'nav' | 'standalone' }} props
 */
const LanguageSwitcher = ({ variant = 'nav' }) => {
  const { locale, setLocale, locales, localeMeta, t } = useI18n();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const select = useCallback(
    (code) => {
      setLocale(code);
      setOpen(false);
    },
    [setLocale]
  );

  return (
    <div
      className={`lang-switch lang-switch--${variant}${open ? ' lang-switch--open' : ''}`}
      ref={rootRef}
    >
      <button
        type="button"
        className="lang-switch__trigger"
        aria-label={t('lang.choose')}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={t('lang.current', { lang: localeMeta.nativeLabel })}
        onClick={() => setOpen((v) => !v)}
      >
        <FlagIcon code={localeMeta.flag} className="lang-switch__flag" />
        <span className="lang-switch__code">{localeMeta.short}</span>
        <FaChevronDown className="lang-switch__caret" aria-hidden="true" />
      </button>

      {open && (
        <ul className="lang-switch__menu" role="listbox" aria-label={t('lang.label')}>
          {locales.map((item) => {
            const active = item.code === locale;
            return (
              <li key={item.code} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  className={`lang-switch__option${active ? ' lang-switch__option--active' : ''}`}
                  onClick={() => select(item.code)}
                >
                  <FlagIcon code={item.flag} className="lang-switch__option-flag" />
                  <span className="lang-switch__option-label">{item.nativeLabel}</span>
                  {active && (
                    <FaCheck className="lang-switch__check" aria-hidden="true" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitcher;
